/**
 * Free Shipping Brands Widget — Animation & Interaction Controller
 *
 * Handles:
 * - Cart widget: MutationObserver for section rendering API updates
 * - Product page: text-based note with info panel (collapsible/popover)
 * - Product page: client-side updates via cart API after add-to-cart
 */

(function () {
  'use strict';

  var ANIM_ENTER = 'fsb-brand--entering';
  var ANIM_EXIT = 'fsb-brand--exiting';
  var ANIM_STAGGER = 'fsb-brand--stagger';

  /* ── Cart widget (drawer/page) ── */

  function snapshotBars(container) {
    var map = new Map();
    var bars = container.querySelectorAll('.fsb-brand[data-vendor]');
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      var fill = el.querySelector('.fsb-bar-fill');
      map.set(el.dataset.vendor, {
        progress: fill ? fill.style.getPropertyValue('--fsb-progress') : '0%',
        achieved: !!el.querySelector('.fsb-status svg'),
        fillColor: el.style.getPropertyValue('--fsb-fill'),
        trackColor: el.style.getPropertyValue('--fsb-track'),
        textColor: el.style.getPropertyValue('--fsb-text')
      });
    }
    return map;
  }

  function createGhostBar(vendor, state) {
    var brand = document.createElement('div');
    brand.className = 'fsb-brand ' + ANIM_EXIT;
    brand.style.setProperty('--fsb-fill', state.fillColor);
    brand.style.setProperty('--fsb-track', state.trackColor);
    brand.style.setProperty('--fsb-text', state.textColor);

    var bar = document.createElement('div');
    bar.className = 'fsb-bar';

    var barFill = document.createElement('div');
    barFill.className = 'fsb-bar-fill';
    barFill.style.setProperty('--fsb-progress', state.progress);

    var barText = document.createElement('div');
    barText.className = 'fsb-bar-text';

    var nameSpan = document.createElement('span');
    nameSpan.className = 'fsb-brand-name';
    nameSpan.textContent = vendor;

    var statusSpan = document.createElement('span');
    statusSpan.className = 'fsb-status';

    barText.appendChild(nameSpan);
    barText.appendChild(statusSpan);
    bar.appendChild(barFill);
    bar.appendChild(barText);
    brand.appendChild(bar);

    return brand;
  }

  function initCartWidget() {
    var container = document.getElementById('free-shipping-brands');
    if (!container || container.hasAttribute('data-product-page')) return;
    if (container.hasAttribute('data-fsb-init')) return;
    container.setAttribute('data-fsb-init', '');

    var snapshot = snapshotBars(container);

    var observer = new MutationObserver(function () {
      var newSnapshot = snapshotBars(container);

      newSnapshot.forEach(function (_state, vendor) {
        if (!snapshot.has(vendor)) {
          var el = container.querySelector('.fsb-brand[data-vendor="' + CSS.escape(vendor) + '"]');
          if (el) {
            el.classList.remove(ANIM_STAGGER);
            el.classList.add(ANIM_ENTER);
            el.addEventListener('animationend', function handler() {
              el.classList.remove(ANIM_ENTER);
              el.removeEventListener('animationend', handler);
            });
          }
        }
      });

      snapshot.forEach(function (state, vendor) {
        if (!newSnapshot.has(vendor)) {
          var widget = container.querySelector('.free-shipping-brands-widget');
          if (widget) {
            var ghost = createGhostBar(vendor, state);
            widget.prepend(ghost);
            ghost.addEventListener('animationend', function () {
              ghost.remove();
            });
          }
        }
      });

      var staggerBars = container.querySelectorAll('.' + ANIM_STAGGER);
      for (var i = 0; i < staggerBars.length; i++) {
        staggerBars[i].classList.remove(ANIM_STAGGER);
        staggerBars[i].style.opacity = '1';
      }

      snapshot = newSnapshot;
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  /* ── Product page: info panel toggle ── */

  function isTouch() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  function initProductInfoAll() {
    var infoBtns = document.querySelectorAll('.fsb-note-info');
    for (var i = 0; i < infoBtns.length; i++) {
      initProductInfoBtn(infoBtns[i]);
    }
  }

  function initProductInfoBtn(infoBtn) {
    if (infoBtn.hasAttribute('data-fsb-init')) return;
    infoBtn.setAttribute('data-fsb-init', '');

    var container = infoBtn.closest('.fsb-product-note');
    if (!container) return;

    var inlinePanel = container.querySelector('.fsb-inline-panel');
    var popover = container.querySelector('.fsb-popover');

    infoBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      var expanded = infoBtn.getAttribute('aria-expanded') === 'true';

      if (isTouch()) {
        if (inlinePanel) {
          inlinePanel.classList.toggle('fsb-inline-panel--open', !expanded);
        }
      } else {
        if (popover) {
          popover.classList.toggle('fsb-popover--open', !expanded);
        }
      }

      infoBtn.setAttribute('aria-expanded', String(!expanded));
    });

    // Close popover on outside click (desktop)
    document.addEventListener('click', function (e) {
      if (popover && popover.classList.contains('fsb-popover--open')) {
        if (!popover.contains(e.target) && !infoBtn.contains(e.target)) {
          popover.classList.remove('fsb-popover--open');
          infoBtn.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  /* ── Product page: client-side cart updates ── */

  function formatMoney(cents, currency) {
    var amount = (cents / 100).toFixed(0);
    amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return amount + ' ' + currency.toLowerCase();
  }

  /**
   * Update the product page note text based on current cart data.
   * Uses safe DOM methods — no innerHTML with untrusted content.
   * The translated template strings come from data attributes set by Liquid (merchant-controlled).
   */
  function updateProductNote(cartData) {
    var containers = document.querySelectorAll('.fsb-product-note[data-product-page]');
    for (var c = 0; c < containers.length; c++) {
      updateSingleProductNote(containers[c], cartData);
    }
  }

  function updateSingleProductNote(container, cartData) {
    var vendor = container.dataset.vendor;
    var threshold = parseInt(container.dataset.threshold, 10);
    var fillColor = container.dataset.fillColor;
    var currency = container.dataset.currency;
    var tInitial = container.dataset.tInitial;
    var tRemaining = container.dataset.tRemaining;
    var tAchieved = container.dataset.tAchieved;

    var vendorTotal = 0;
    if (cartData && cartData.items) {
      for (var i = 0; i < cartData.items.length; i++) {
        if (cartData.items[i].vendor === vendor) {
          vendorTotal += cartData.items[i].final_line_price;
        }
      }
    }

    var noteEl = container.querySelector('.fsb-note');
    var textEl = container.querySelector('.fsb-note-text');
    var dotEl = container.querySelector('.fsb-dot');
    if (!noteEl || !textEl || !dotEl) return;

    var achieved = vendorTotal >= threshold;

    if (achieved) {
      noteEl.classList.add('fsb-note--achieved');
      dotEl.style.background = '#2d6a30';
      textEl.textContent = tAchieved;
    } else if (vendorTotal > 0) {
      noteEl.classList.remove('fsb-note--achieved');
      dotEl.style.background = fillColor;
      var deficit = threshold - vendorTotal;
      var deficitFormatted = formatMoney(deficit, currency);
      textEl.textContent = '';
      var parts = tRemaining.split('__AMOUNT__');
      if (parts.length === 2) {
        textEl.appendChild(document.createTextNode(parts[0]));
        var strong = document.createElement('strong');
        strong.textContent = deficitFormatted;
        textEl.appendChild(strong);
        textEl.appendChild(document.createTextNode(parts[1]));
      } else {
        textEl.textContent = tRemaining.replace('__AMOUNT__', deficitFormatted);
      }
    } else {
      noteEl.classList.remove('fsb-note--achieved');
      dotEl.style.background = fillColor;
      textEl.textContent = tInitial;
    }
  }

  function initProductCartListener() {
    var container = document.getElementById('free-shipping-brands-product');
    if (!container || container.hasAttribute('data-fsb-cart-init')) return;
    container.setAttribute('data-fsb-cart-init', '');

    // Intercept fetch to detect cart mutations and update the note
    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      return originalFetch.apply(this, args).then(function (response) {
        var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear')) {
          originalFetch.call(window, '/cart.js', {
            headers: { 'Content-Type': 'application/json' }
          })
            .then(function (r) { return r.json(); })
            .then(updateProductNote)
            .catch(function () {});
        }
        return response;
      });
    };
  }

  /* ── Inline bars (cart drawer brand-grouped) ── */

  /**
   * Convert <tr class="fsb-inline-row"> elements inside the cart table
   * into <div> elements placed directly before the table, wrapping each
   * brand's product rows. Tables cannot reliably render arbitrary <tr>
   * elements with full width when columns use display:block/flex.
   */
  function initInlineBars() {
    var tables = document.querySelectorAll('#cart-items table, cart-items table');
    for (var t = 0; t < tables.length; t++) {
      var table = tables[t];
      if (table.hasAttribute('data-fsb-inline-init')) continue;
      table.setAttribute('data-fsb-inline-init', '');

      var inlineRows = table.querySelectorAll('tr.fsb-inline-row');
      if (!inlineRows.length) continue;

      // Wrap table in a relative container if not already
      var wrapper = table.parentElement;
      var wrapDiv = document.createElement('div');
      wrapDiv.className = 'fsb-inline-wrapper';
      wrapDiv.style.cssText = 'position:relative;';
      wrapper.insertBefore(wrapDiv, table);
      wrapDiv.appendChild(table);

      for (var i = 0; i < inlineRows.length; i++) {
        var inlineRow = inlineRows[i];
        var vendor = inlineRow.dataset.vendor;
        var barDiv = inlineRow.querySelector('.fsb-inline-bar');
        if (!barDiv) continue;

        // Clone bar content as a standalone div
        var outerDiv = document.createElement('div');
        outerDiv.className = 'fsb-inline-bar-outer';
        outerDiv.setAttribute('data-vendor', vendor);
        outerDiv.innerHTML = barDiv.outerHTML;

        // Find the first cart-item row for this vendor to insert before
        var cartItemRows = table.querySelectorAll('tr.cart-item');
        var targetRow = null;
        for (var j = 0; j < cartItemRows.length; j++) {
          var rowVendor = cartItemRows[j].querySelector('.product-option');
          // Use data-vendor attribute if present, else check vendor text
          var rowVendorAttr = cartItemRows[j].dataset.vendor ||
            cartItemRows[j].querySelector('[data-vendor]')?.dataset.vendor;
          if (!rowVendorAttr) {
            // Try to match via vendor text in the row
            var vendorEl = cartItemRows[j].querySelector('.cart-item__vendor, [class*="vendor"]');
            if (vendorEl && vendorEl.textContent.trim().toLowerCase() === vendor.toLowerCase()) {
              targetRow = cartItemRows[j];
              break;
            }
          } else if (rowVendorAttr.toLowerCase() === vendor.toLowerCase()) {
            targetRow = cartItemRows[j];
            break;
          }
        }

        // Insert outerDiv before the table (as a sibling, before the wrapper)
        // Position it visually above the first matching cart-item row
        if (targetRow) {
          // Insert as a row-like div before targetRow using a wrapper approach
          var rowWrapper = document.createElement('div');
          rowWrapper.className = 'fsb-inline-bar-row';
          rowWrapper.appendChild(outerDiv);
          // Insert before the table itself, we'll use CSS order
          table.parentElement.insertBefore(rowWrapper, table);
        } else {
          // Fallback: insert before table
          var rowWrapper2 = document.createElement('div');
          rowWrapper2.className = 'fsb-inline-bar-row';
          rowWrapper2.appendChild(outerDiv);
          table.parentElement.insertBefore(rowWrapper2, table);
        }

        // Remove the original <tr> from the table
        inlineRow.remove();
      }
    }
  }

  /* ── Boot ── */

  function init() {
    initCartWidget();
    initProductInfoAll();
    initProductCartListener();
    initInlineBars();
  }

  // Re-init when new content is dynamically injected (e.g. quick-add drawer)
  function watchForNewWidgets() {
    var observer = new MutationObserver(function () {
      initProductInfoAll();
      initProductCartListener();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      watchForNewWidgets();
    });
  } else {
    init();
    watchForNewWidgets();
  }
})();
